<script setup lang="ts">
import { useField, useForm } from 'vee-validate';
import { ref } from 'vue';

import { UserService } from '../../api/user/userService';
import { router } from '../../Router';
import { RoutePaths } from '../../Router/types/routePaths';
import { useUserStore } from '../../stores/userStore/userStore';
import { useUserTokensStore } from '../../stores/userTokensStore/userTokensStore';
import LoginZodSchema from '../../pages/Schemas/LoginZodValidationSchema';
import { validateSchema } from '../../pages/Schemas/Schemas';
import ButtonWithSpinner from '../UI/ButtonWithSpinner.vue';

const buttonState = ref<'pending' | 'success' | 'error'>('pending');
const loginError = ref<string | null>(null);
const isSubmitting = ref(false);
const showPassword = ref(false);

const userTokensStore = useUserTokensStore();
const userStore = useUserStore();

const { handleSubmit: handleLoginSubmit } = useForm({
  validationSchema: validateSchema(LoginZodSchema),
  initialValues: {
    login: '',
    password: '',
  },
});

const { value: loginName, errorMessage: loginNameError } = useField('login');
const { value: loginPassword, errorMessage: loginPasswordError } = useField('password');

const onLoginSubmit = handleLoginSubmit(async (values) => {
  const { login, password } = values;
  buttonState.value = 'pending';
  isSubmitting.value = true;
  loginError.value = null;

  try {
    const response = await UserService.login({ login, password });
    buttonState.value = 'success';
    userTokensStore.setUserTokens(response);
    const user = await UserService.getMe();
    userStore.setUser(user);
    router.push({
      path: RoutePaths.dashboard,
      replace: true,
    });
  } catch (error) {
    buttonState.value = 'error';
    if (error instanceof Error) {
      loginError.value =
        error.message === 'Invalid credentials'
          ? 'Niepoprawne dane logowania'
          : 'Nie udało się zalogować. Spróbuj ponownie';
    }
  } finally {
    isSubmitting.value = false;
  }
});

defineEmits<{
  (e: 'toggleForm'): void;
}>();
</script>

<template>
  <form
    class="login-form"
    @submit="onLoginSubmit"
  >
    <div class="login-form__group">
      <label
        for="login"
        class="login-form__label"
        >Login</label
      >
      <input
        id="login"
        v-model="loginName"
        class="login-form__input"
        :class="{ 'login-form__input--error': loginNameError }"
        type="text"
        placeholder="Wprowadź login"
      />
      <div
        v-if="loginNameError"
        class="login-form__message"
      >
        <small class="login-form__error">{{ loginNameError }}</small>
      </div>
    </div>

    <div class="login-form__group">
      <label
        for="password"
        class="login-form__label"
        >Hasło</label
      >
      <div class="login-form__input-wrapper">
        <input
          id="password"
          v-model="loginPassword"
          :type="showPassword ? 'text' : 'password'"
          class="login-form__input"
          :class="{ 'login-form__input--error': loginPasswordError }"
          placeholder="Wprowadź hasło"
        />
        <button
          type="button"
          class="login-form__toggle-password"
          @click="showPassword = !showPassword"
        >
          <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
        </button>
      </div>
      <div
        v-if="loginPasswordError"
        class="login-form__message"
      >
        <small class="login-form__error">{{ loginPasswordError }}</small>
      </div>
    </div>

    <ButtonWithSpinner
      type="submit"
      :is-loading="isSubmitting"
      :text="'ZALOGUJ SIĘ'"
      :variant="buttonState"
    />

    <div
      v-if="loginError"
      class="login-form__message login-form__message--center"
    >
      <small class="login-form__error">{{ loginError }}</small>
    </div>

    <div class="login-form__links">
      <button
        type="button"
        class="login-form__link"
        @click="$emit('toggleForm')"
      >
        Nie masz konta? Zarejestruj się
      </button>
      <router-link
        to="/request-password-reset"
        class="login-form__forgot-password"
      >
        Zapomniałeś hasła?
      </router-link>
    </div>
  </form>
</template>

<style scoped lang="scss">
@use 'sass:color';

.login-form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding: 1.5rem;
  background: $white;
  border-radius: 1rem;

  &__group {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    position: relative;
  }

  &__label {
    font-size: 2rem;
    font-weight: 700;
    color: $black-400;
    transition: color 0.2s ease;
  }

  &__input {
    height: 3.5rem;
    padding: 0 1.25rem;
    border: 2px solid $brown-400;
    border-radius: 0.75rem;
    font-size: 1.75rem;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background-color: $white;
    color: $black-400;
    width: 100%;

    &::placeholder {
      color: $brown-700;
      font-size: 1.5rem;
      opacity: 0.7;
    }

    &:focus {
      outline: none;
      border-color: $brown-200;
      box-shadow: 0 0 0 4px rgba($brown-200, 0.15);
    }

    &--error {
      border-color: #f33;

      &:focus {
        box-shadow: 0 0 0 4px rgb(255 51 51 / 15%);
      }
    }
  }

  &__message {
    min-height: 1.25rem;
    margin-top: 0.25rem;

    &--center {
      text-align: center;
    }
  }

  &__error {
    font-size: 1.5rem;
    color: #f33;
    display: block;
    font-weight: 500;
  }

  &__button {
    height: 4.5rem;
    background: $brown-200;
    color: $white;
    border: none;
    border-radius: 0.75rem;
    font-size: 1.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    margin-top: 1rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &:hover {
      background: color.adjust($brown-200, $lightness: -5%);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba($brown-200, 0.3);
    }

    &:active {
      background: color.adjust($brown-200, $lightness: -10%);
      transform: translateY(0);
      box-shadow: 0 2px 6px rgba($brown-200, 0.2);
    }

    &--pending {
      background: $brown-200;
    }

    &--success {
      background: #4caf50;
    }

    &--error {
      background: #f44336;
    }
  }

  &__links {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    align-items: center;
    margin-top: 1rem;
  }

  &__link {
    font-size: 1.75rem;
    color: $brown-200;
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 0.75rem 1.5rem;
    border-radius: 0.5rem;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    letter-spacing: 0.3px;
    line-height: 1.4;

    &:hover {
      color: $brown-100;
      background-color: rgba($brown-200, 0.1);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &__forgot-password {
    font-size: 1.5rem;
    color: $brown-200;
    text-decoration: none;
    transition: color 0.2s;
    padding: 0.5rem;

    &:hover {
      opacity: 0.8;
    }
  }

  &__input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__toggle-password {
    position: absolute;
    right: 1rem;
    background: none;
    border: none;
    color: $brown-300;
    cursor: pointer;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;

    &:hover {
      color: $brown-200;
    }

    i {
      font-size: 1.6rem;
    }
  }
}
</style>
